import { EVENTS } from "../../../../../js/constants";

/**
 * Represents a PaymentAmount Element with two fieldsets
 * the first fieldset radios define which of the second fieldset radios are visible
 * The logic to hide / show elements happens via Drupal States
 *
 * But some browsers have issues with keyboard usage when there are
 * hidden AND visible radio buttons with the same value in name attribute
 */
class PaymentAmount {
  /**
   * @param {HTMLElement} element - payment amount element
   */
  constructor(element) {
    this.element = element;

    this.amount = this.element.querySelector(
      ".PaymentAmount-amount, .js-DonationTeaser-amount, .js-Stage-amount"
    );
    this.interval = this.element.querySelector(
      ".PaymentAmount-interval, .js-DonationTeaser-interval, .js-Stage-interval"
    );
    this.intervalHolder = this.element.querySelector(
      ".js-PaymentAmount-intervalHolder"
    );
    this.intervalFlyout = this.element.querySelector(
      ".js-PaymentAmount-flyout"
    );
    this.openedFlyoutClass = "is-flyoutOpened";
    this.intervalInputs = [
      ...this.interval.querySelectorAll(".FskRadio, .FskInput--select"),
    ];
    this.amountRadios = [...this.amount.querySelectorAll(".FskRadio")];
    this.amountTextInput = this.amount.querySelector(".FskInput");
    this.donationExamples = this.element.querySelector(
      ".js-PaymentAmount-examples"
    );

    this._saveAmountNamesToDataAttributes();
    this._setOrderOfAmountButtons();
    this._checkForDonationExample();
    this._bind();
  }

  /**
   * init
   *
   * @private
   */
  _bind() {
    if (this.intervalFlyout) {
      this.intervalFlyout.addEventListener(EVENTS.FLYOUT_OPEN, (event) => {
        if (!event.detail.isOpen) {
          this.intervalHolder.classList.remove(this.openedFlyoutClass);
        }
      });
    }

    this.intervalInputs.forEach((option) => {
      option.addEventListener("change", () => {
        if (option.type === "radio") {
          this._setExclusiveAttribute(option, "aria-selected");
        }
        this._setDefaultPaymentAmount();
        this._handleNameAttributeOnChange();

        if (this.intervalFlyout) {
          this._checkForOneTimePayment(option);
        }

        this._checkForDonationExample();
      });
    });

    this.amountRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        // remove value from input field since the value is defined by the radio
        this.amountTextInput.value = "";
        this.amountTextInput.classList.remove("is-touched");

        this._checkForDonationExample();
      });
    });

    this.amountTextInput.addEventListener("input", () => {
      if (this.amountTextInput.value) {
        // remove checked state from radio since the value is defined by the input field
        const checkedItem = this.amountRadios.find((r) => r.checked);

        if (checkedItem) {
          checkedItem.checked = false;
        }
      } else {
        this._setDefaultPaymentAmount();
      }

      this._checkForDonationExample();
    });
  }

  /**
   * sets an attribute exclusively only for the passed element and removes it everywhere else
   *
   * @param {HTMLElement} element
   * @param {string} attribute - a boolean html attribute e.g. aria-selected
   * @private
   */
  _setExclusiveAttribute(element, attribute) {
    const prevElement = this.element.querySelector(`[${attribute}]`);

    if (prevElement) {
      prevElement.removeAttribute(attribute);
    }
    element.setAttribute(attribute, "true");
  }

  /**
   * sets a custom property to all amount buttons
   * so that they can be ordered with flexbox based on their value
   *
   * @private
   */
  _setOrderOfAmountButtons() {
    const radios = this.amountRadios;
    radios.sort((a, b) => a.value - b.value);

    radios.forEach((radio, i) =>
      radio
        .closest(".FskFormElement")
        .style.setProperty(`--FskFormElement-order`, i)
    );
  }

  /**
   * sorts the visible items based on input value
   * and sets default payment amount to the second visible item
   *
   * @private
   */
  _setDefaultPaymentAmount() {
    const visibleItems = this.amountRadios.filter(
      (amountRadio) =>
        amountRadio.closest(".FskFormElement").style.display !== "none"
    );

    visibleItems.sort((a, b) => a.value - b.value);

    const secondItem = visibleItems[1];

    if (secondItem) {
      secondItem.checked = true;
      this.amountTextInput.value = "";
      this.amountTextInput.classList.remove("is-touched");
    }
  }

  /**
   * adds or removes name attribute based on visibility for better browser support
   *
   * @private
   */
  _handleNameAttributeOnChange() {
    this.amountRadios.forEach((amountRadio) => {
      const { display } = amountRadio.closest(".FskFormElement").style;
      const name = amountRadio.getAttribute("data-name");
      amountRadio.setAttribute("name", display === "none" ? "" : name);
    });
  }

  /**
   * saves value of name attribute of amount inputs to dedicated data attributes
   *
   * @private
   */
  _saveAmountNamesToDataAttributes() {
    this.amountRadios.forEach((amountRadio) => {
      amountRadio.setAttribute("data-name", amountRadio.name);
    });
  }

  /**
   * hide/shows flyout and dispatch custom event depending on the radio value
   *
   * @param {object} option - checked radiobutton or selected option in case of the <select> element
   * @private
   */
  _checkForOneTimePayment(option) {
    const triggeredElement =
      option.type === "radio"
        ? this.element.querySelector(`[for="${option.id}"]`)
        : option;
    const isOneTimePayment = option.value === "0";
    this.intervalHolder.classList.toggle(
      this.openedFlyoutClass,
      isOneTimePayment
    );

    this.intervalFlyout.dispatchEvent(
      new CustomEvent(EVENTS.FLYOUT_OPEN, {
        detail: {
          isOpen: isOneTimePayment,
          triggeredElement,
        },
      })
    );
  }

  /**
   * returns currently selected interval
   *
   * @private
   * @returns {string} - selected interval
   */
  _getCurrentInterval() {
    const select = this.interval?.querySelector("select");
    const input = this.interval?.querySelector("input:checked");

    return select ? select.value : input?.value;
  }

  /**
   * returns currently selected amount
   *
   * @private
   * @returns {string} - selected amount
   */
  _getCurrentAmount() {
    const amountFallback = "donate_custom_amount";
    const input = this.amount?.querySelector("input:checked");

    return input?.value ? input.value : amountFallback;
  }

  /**
   * shows donation example for currently selected interval/amount combination (if available)
   *
   * @private
   */
  _checkForDonationExample() {
    if (!this.donationExamples) return;

    const interval = this._getCurrentInterval();
    const amount = this._getCurrentAmount();

    if (!(interval && amount)) return;

    const prevSelector = ".js-DonationExample";
    const nextSelector = `.js-DonationExampleTemplate[data-interval-dependency="${interval}"][data-amount-dependency="${amount}"]`;

    const prevExample = this.donationExamples?.querySelector(prevSelector);

    if (prevExample) {
      this.donationExamples.removeChild(prevExample);
    }

    const nextExample = this.donationExamples?.querySelector(nextSelector);

    if (nextExample) {
      const nextExampleContent =
        nextExample.content.firstElementChild.cloneNode(true);

      if (nextExampleContent) {
        this.donationExamples.appendChild(nextExampleContent);
      }
    }
  }
}

export default PaymentAmount;
